import { useState, useEffect, useRef, useCallback } from 'react';

const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

// Minimal react-query-compatible data-fetching hook. Tracks the request lifecycle
// (loading/data/error) and reflects the result of `queryFn`. Caching, if any, is
// owned by `queryFn` — this hook stays cache-agnostic.
const useQuery = ({ queryKey, queryFn, enabled = true }) => {
  // An enabled query is "pending" from the first render (react-query semantics): the fetch is
  // kicked off in an effect, but `isLoading` must already be true on mount so consumers never
  // mistake the pre-fetch render for a settled, empty result. Disabled queries stay idle.
  const [state, setState] = useState(() => ({
    data: undefined,
    error: null,
    status: enabled ? STATUS.LOADING : STATUS.IDLE,
  }));

  // Keep the latest queryFn in a ref so its identity changing between renders does
  // not re-trigger the effect (only queryKey/enabled should).
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  const execute = useCallback(() => {
    let cancelled = false;

    setState((prev) => ({ ...prev, status: STATUS.LOADING, error: null }));

    const promise = Promise.resolve()
      .then(() => queryFnRef.current())
      .then((data) => {
        if (!cancelled) {
          setState({ data, error: null, status: STATUS.SUCCESS });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, error, status: STATUS.ERROR }));
        }
      });

    return {
      promise,
      cancel: () => {
        cancelled = true;
      },
    };
  }, []);

  const serializedKey = JSON.stringify(queryKey ?? null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const run = execute();

    return run.cancel;
  }, [serializedKey, enabled, execute]);

  const refetch = useCallback(() => execute().promise, [execute]);

  const { data, error, status } = state;

  return {
    data,
    error,
    status,
    isLoading: status === STATUS.LOADING,
    isError: status === STATUS.ERROR,
    isSuccess: status === STATUS.SUCCESS,
    isFetching: status === STATUS.LOADING,
    refetch,
  };
};

export { STATUS };
export default useQuery;
